import * as d3 from 'd3';
import { scalePow, scaleLinear } from 'd3-scale';

function overflowEllipse(that: any, maxWidth: number) {
  const self = d3.select(that);
  let text = self.text();
  while (self.node().getComputedTextLength() > maxWidth && text.length > 1) {
    text = text.slice(0, -1);
    self.text(`${text}...`);
  }
  if (text.length === 1) {
    self.text(text);
  }
}

// This is ugly, maybe invest some time into wrapping drawing elements
// like boxplots and bar groups into functions.
// Con: Most of these are one-of visualizations.

export function drawChart(
  contentRef: any,
  contentWidth: number | undefined,
  contentHeight: number | undefined,
  aggregate: boolean,
  aggregatedResults: any,
  results: any,
  showFordFulkerson: boolean,
  showEdmondsKarp: boolean,
  showDinic: boolean,
  showGoldbergTarjan: boolean,
  useLinearYAxis: boolean
) {
  const rowHeight = 20;
  const labelWidth = 160;
  const labelPaddingLeft = 10;
  const labelPaddingBottom = 10;
  const failColor = '#e33e19';
  const okColor = '#069e15';
  const algoColors = ['#745F53', '#657153', '#566271', '#6D4C6C'];

  const barSlotWrapperWidth =
    ((contentWidth || 0) - labelWidth - labelPaddingLeft) /
    (aggregate ? aggregatedResults : results).length;
  const barSlotHeight =
    (contentHeight || 0) - (7 + (aggregate ? 1 : 0)) * rowHeight - 3 * labelPaddingBottom;
  const barPadding = 2;
  const barSlotWidth = barSlotWrapperWidth - barPadding;
  const fontSize = '15px';

  const fordFulkersonMaxRuntime = showFordFulkerson
    ? Math.max(...results.map((r: any) => r.individualResults[0].runtime))
    : 0;
  const edmondsKarpMaxRuntime = showEdmondsKarp
    ? Math.max(...results.map((r: any) => r.individualResults[1].runtime))
    : 0;
  const dinicMaxRuntime = showDinic
    ? Math.max(...results.map((r: any) => r.individualResults[2].runtime))
    : 0;
  const goldbergTarjanMaxRuntime = showGoldbergTarjan
    ? Math.max(...results.map((r: any) => r.individualResults[3].runtime))
    : 0;
  const maxRuntime = Math.max(
    fordFulkersonMaxRuntime,
    edmondsKarpMaxRuntime,
    dinicMaxRuntime,
    goldbergTarjanMaxRuntime
  );
  const runtimeScale = useLinearYAxis
    ? scaleLinear([maxRuntime, 0], [0, barSlotHeight])
    : scalePow([maxRuntime, 0], [0, barSlotHeight]).exponent(1 / 3);
  const runtimeAxis = d3.axisLeft(runtimeScale).ticks(10);
  const minNoInstances = aggregate
    ? Math.min(...aggregatedResults.map((r: any) => r.noInstances))
    : 0;
  const maxNoInstances = aggregate
    ? Math.max(...aggregatedResults.map((r: any) => r.noInstances))
    : 1;
  const noInstancesColor = d3
    .scaleLinear()
    .domain([minNoInstances, maxNoInstances])
    .range(['#109bd2', '#e00b35'] as any)
    .interpolate(d3.interpolateHcl as any);
  const minNoNodes = Math.min(...results.map((r: any) => r.noNodes));
  const maxNoNodes = Math.max(...results.map((r: any) => r.noNodes));
  const noNodesColor = d3
    .scaleLinear()
    .domain([minNoNodes, maxNoNodes])
    .range(['#109bd2', '#e00b35'] as any)
    .interpolate(d3.interpolateHcl as any);
  const minMaxCapacity = Math.min(...results.map((r: any) => r.maxCapacity));
  const maxMaxCapacity = Math.max(...results.map((r: any) => r.maxCapacity));
  const capacityColor = d3
    .scaleLinear()
    .domain([minMaxCapacity, maxMaxCapacity])
    .range(['#109bd2', '#e00b35'] as any)
    .interpolate(d3.interpolateHcl as any);

  const noDisplayedAlgorithms =
    (showFordFulkerson ? 1 : 0) +
    (showEdmondsKarp ? 1 : 0) +
    (showDinic ? 1 : 0) +
    (showGoldbergTarjan ? 1 : 0);

  const barWidth =
    barSlotWidth / noDisplayedAlgorithms -
    ((noDisplayedAlgorithms - 1) / noDisplayedAlgorithms) * barPadding;

  const getAlgoIndex = (j: number): number => {
    const t = [
      [],
      [0],
      [1],
      [0, 1],
      [2],
      [0, 2],
      [1, 2],
      [0, 1, 2],
      [3],
      [0, 3],
      [1, 3],
      [0, 1, 3],
      [2, 3],
      [0, 2, 3],
      [1, 2, 3],
      [0, 1, 2, 3]
    ];
    let i = 0;
    if (showFordFulkerson) i += 1;
    if (showEdmondsKarp) i += 2;
    if (showDinic) i += 4;
    if (showGoldbergTarjan) i += 8;
    return t[i][j];
  };

  d3.select(contentRef.current).select('svg').remove();

  const svg = d3.select(contentRef.current).append('svg');
  svg.attr('width', contentWidth || 0);
  svg.attr('height', contentHeight || 0);

  const runtimeAxisEl = svg
    .append('g')
    .classed('runtime-axis', true)
    .style('transform', `translate(${labelWidth}px,${labelPaddingBottom}px)`)
    .call(runtimeAxis);

  const runtimeAxisLabel = svg
    .append('text')
    .text('Runtime [ms]')
    .attr('text-anchor', 'end')
    .style('transform', `translate(${labelWidth - 40}px,${labelPaddingBottom}px) rotate(-90deg)`)
    .style('font-family', 'Helvetica Neue, Helvetica, Arial')
    .style('font-size', fontSize);

  if (aggregate) {
    const boxPlots = svg.selectAll('g.runtime').data(aggregatedResults).join('g');
    for (let j = 0; j < noDisplayedAlgorithms; j += 1) {
      const k = getAlgoIndex(j);
      // vertical line
      boxPlots
        .append('line')
        .attr(
          'x1',
          (d, i) =>
            labelPaddingLeft +
            labelWidth +
            i * barSlotWrapperWidth +
            (barWidth + barPadding) * j +
            barWidth / 2
        )
        .attr(
          'x2',
          (d, i) =>
            labelPaddingLeft +
            labelWidth +
            i * barSlotWrapperWidth +
            (barWidth + barPadding) * j +
            barWidth / 2
        )
        .attr(
          'y1',
          (d: any) => runtimeScale(d.individualResults[k].runtime.min) + labelPaddingBottom
        )
        .attr(
          'y2',
          (d: any) => runtimeScale(d.individualResults[k].runtime.max) + labelPaddingBottom
        )
        .attr('stroke', 'black');
      // box
      boxPlots
        .append('rect')
        .attr(
          'x',
          (d, i) =>
            labelPaddingLeft + labelWidth + i * barSlotWrapperWidth + (barWidth + barPadding) * j
        )
        .attr('width', barWidth)
        .attr('y', (d: any) => runtimeScale(d.individualResults[k].runtime.q3) + labelPaddingBottom)
        .attr(
          'height',
          (d: any) =>
            runtimeScale(d.individualResults[k].runtime.q1) -
            runtimeScale(d.individualResults[k].runtime.q3)
        )
        .attr('fill', algoColors[k])
        .attr('stroke', 'black');
      // min, median and max horizontal line
      for (const minMax of ['min', 'median', 'max']) {
        boxPlots
          .append('line')
          .attr(
            'x1',
            (d, i) =>
              labelPaddingLeft + labelWidth + i * barSlotWrapperWidth + (barWidth + barPadding) * j
          )
          .attr(
            'x2',
            (d, i) =>
              labelPaddingLeft +
              labelWidth +
              i * barSlotWrapperWidth +
              (barWidth + barPadding) * j +
              barWidth
          )
          .attr(
            'y1',
            (d: any) => runtimeScale(d.individualResults[k].runtime[minMax]) + labelPaddingBottom
          )
          .attr(
            'y2',
            (d: any) => runtimeScale(d.individualResults[k].runtime[minMax]) + labelPaddingBottom
          )
          .attr('stroke', 'black');
      }
      if (barWidth > 18) {
        boxPlots
          .append('text')
          .text(['Ford-Fulkerson', 'Edmonds-Karp', 'Dinic', 'Goldberg-Tarjan'][k])
          .attr(
            'x',
            (d, i) =>
              labelPaddingLeft + labelWidth + i * barSlotWrapperWidth + (barWidth + barPadding) * j
          )
          .attr('y', (d: any) =>
            runtimeScale(d.individualResults[k].runtime.max) > 30
              ? runtimeScale(d.individualResults[k].runtime.max)
              : runtimeScale(d.individualResults[k].runtime.min) + 30
          )
          .style('font-family', 'Helvetica Neue, Helvetica, Arial')
          .style('font-size', fontSize)
          .each(function () {
            overflowEllipse(this, barWidth);
          });
      }
    }
  } else {
    const runtimeBars = svg.selectAll('g.runtime-bar').data(results).join('g');
    for (let j = 0; j < noDisplayedAlgorithms; j += 1) {
      const k = getAlgoIndex(j);
      runtimeBars
        .append('rect')
        .classed('runtime-bar', true)
        .attr(
          'x',
          (d, i) =>
            labelPaddingLeft + labelWidth + i * barSlotWrapperWidth + (barWidth + barPadding) * j
        )
        .attr('y', (d: any) => runtimeScale(d.individualResults[k].runtime) + labelPaddingBottom)
        .attr('width', barWidth)
        .attr('height', (d: any) => barSlotHeight - runtimeScale(d.individualResults[k].runtime))
        .attr('fill', algoColors[k]);
      if (barWidth > 18) {
        runtimeBars
          .append('text')
          .text(['Ford-Fulkerson', 'Edmonds-Karp', 'Dinic', 'Goldberg-Tarjan'][k])
          .style(
            'transform',
            (d, i) =>
              `translate(${
                labelPaddingLeft +
                labelWidth +
                i * barSlotWrapperWidth +
                (barWidth + barPadding) * j
              }px,${barSlotHeight + labelPaddingBottom}px) rotate(-90deg) translate(0.2em, 0.95em)`
          )
          .style('font-family', 'Helvetica Neue, Helvetica, Arial')
          .style('font-size', fontSize)
          .attr('fill', 'white');
      }
    }
  }
  const labels = svg
    .append('g')
    .selectAll('text')
    .data([
      'Same Flow',
      'Preservation',
      'Capacity',
      'Saturated Cut',
      'Capacity',
      'Iterations',
      ...(aggregate ? ['Instances'] : [])
    ])
    .join('text')
    .attr('x', labelPaddingLeft)
    .attr('y', (d, i) => (contentHeight || 0) - i * rowHeight - labelPaddingBottom - 7)
    .attr('width', labelWidth)
    .attr('height', rowHeight)
    .style('font-family', 'Helvetica Neue, Helvetica, Arial')
    .style('font-size', fontSize)
    .text((d) => d);

  const statusBoxStacks = svg
    .append('g')
    .style(
      'transform',
      `translate(${labelPaddingLeft + labelWidth}px,${barSlotHeight + 2 * labelPaddingBottom}px)`
    )
    .attr('width', (contentWidth || 0) - labelPaddingLeft - labelWidth)
    .attr('height', (contentHeight || 0) - barSlotHeight)
    .selectAll('g')
    .data(aggregate ? (aggregatedResults as any) : (results as any))
    .join('g')
    .style('transform', (d, i) => `translate(${i * barSlotWrapperWidth}px,${0}px)`);

  statusBoxStacks.each(function (d: any, i) {
    let g = d3.select(this).append('g');
    // #Instances
    if (aggregate) {
      g.append('rect')
        .attr('y', rowHeight)
        .attr('width', barSlotWidth)
        .attr('height', rowHeight - barPadding)
        .attr('fill', () => noInstancesColor(d.noInstances));
      g.append('text')
        .attr('x', () => {
          if (i === 0 && barSlotWidth <= `${d.noInstances}`.length * 9) return 0;
          if (i === aggregatedResults.length - 1 && barSlotWidth <= `${d.noInstances}`.length * 9)
            return barSlotWidth;
          return barSlotWidth / 2;
        })
        .attr('y', 2 * rowHeight - 5)
        .text((d: any) =>
          barSlotWidth > `${d.noInstances}`.length * 9 ||
          i === 0 ||
          i === aggregatedResults.length - 1
            ? d.noInstances
            : ''
        )
        .style('font-size', fontSize)
        .attr('text-anchor', () => {
          if (i === 0 && barSlotWidth <= `${d.noInstances}`.length * 9) return 'start';
          if (i === aggregatedResults.length - 1 && barSlotWidth <= `${d.noInstances}`.length * 9)
            return 'end';
          return 'middle';
        });
    }
    // # Iterations
    g.append('rect')
      .attr('y', (1 + (aggregate ? 1 : 0)) * rowHeight)
      .attr('width', barSlotWidth)
      .attr('height', rowHeight - barPadding)
      .attr('fill', () => noNodesColor(d.noNodes));
    g.append('text')
      .attr('x', () => {
        if (i === 0 && barSlotWidth <= `${d.noNodes}`.length * 9) return 0;
        if (
          i === (aggregate ? aggregatedResults : results).length - 1 &&
          barSlotWidth <= `${d.noNodes}`.length * 9
        )
          return barSlotWidth;
        return barSlotWidth / 2;
      })
      .attr('y', (2 + (aggregate ? 1 : 0)) * rowHeight - 5)
      .text((d: any) =>
        barSlotWidth > `${d.noNodes}`.length * 9 ||
        i === 0 ||
        i === (aggregate ? aggregatedResults : results).length - 1
          ? d.noNodes
          : ''
      )
      .style('font-size', fontSize)
      .attr('text-anchor', () => {
        if (i === 0 && barSlotWidth <= `${d.noNodes}`.length * 9) return 'start';
        if (
          i === (aggregate ? aggregatedResults : results).length - 1 &&
          barSlotWidth <= `${d.noNodes}`.length * 9
        )
          return 'end';
        return 'middle';
      });
    // Max Capacity
    g = d3.select(this).append('g');
    g.append('rect')
      .attr('y', (2 + (aggregate ? 1 : 0)) * rowHeight)
      .attr('width', barSlotWidth)
      .attr('height', rowHeight - barPadding)
      .attr('fill', (d: any) => capacityColor(d.maxCapacity));
    g.append('text')
      .attr('x', barSlotWidth / 2)
      .attr('y', (3 + (aggregate ? 1 : 0)) * rowHeight - 5)
      .text((d: any) => (barSlotWidth > `${d.maxCapacity}`.length * 9 ? d.maxCapacity : ''))
      .style('font-size', fontSize)
      .attr('text-anchor', 'middle');
    for (let j = 0; j < noDisplayedAlgorithms; j += 1) {
      const k = getAlgoIndex(j);
      g.append('rect')
        .attr('x', j * (barWidth + barPadding))
        .attr('y', (3 + (aggregate ? 1 : 0)) * rowHeight)
        .attr('width', barWidth)
        .attr('height', rowHeight - barPadding)
        .attr('fill', (d: any) =>
          d.individualResults[k].hasSaturatedCut.ok ? okColor : failColor
        );
      g.append('text')
        .attr('x', j * (barWidth + barPadding) + barWidth / 2)
        .attr('y', (4 + (aggregate ? 1 : 0)) * rowHeight - 4)
        .attr('width', barWidth)
        .attr('height', rowHeight - barPadding)
        .attr('text-anchor', 'middle')
        .text((d: any) => (d.individualResults[k].hasSaturatedCut.ok ? '' : 'f'));
      g.append('rect')
        .attr('x', j * (barWidth + barPadding))
        .attr('y', (4 + (aggregate ? 1 : 0)) * rowHeight)
        .attr('width', barWidth)
        .attr('height', rowHeight - barPadding)
        .attr('fill', (d: any) =>
          d.individualResults[k].satisfiesCapacity.ok ? okColor : failColor
        );
      g.append('text')
        .attr('x', j * (barWidth + barPadding) + barWidth / 2)
        .attr('y', (5 + (aggregate ? 1 : 0)) * rowHeight - 4)
        .attr('width', barWidth)
        .attr('height', rowHeight - barPadding)
        .attr('text-anchor', 'middle')
        .text((d: any) => (d.individualResults[k].satisfiesCapacity.ok ? '' : 'f'));
      g.append('rect')
        .attr('x', j * (barWidth + barPadding))
        .attr('y', (5 + (aggregate ? 1 : 0)) * rowHeight)
        .attr('width', barWidth)
        .attr('height', rowHeight - barPadding)
        .attr('fill', (d: any) =>
          d.individualResults[k].satisfiesPreservation.ok ? okColor : failColor
        );
      g.append('text')
        .attr('x', j * (barWidth + barPadding) + barWidth / 2)
        .attr('y', (6 + (aggregate ? 1 : 0)) * rowHeight - 4)
        .attr('width', barWidth)
        .attr('height', rowHeight - barPadding)
        .attr('text-anchor', 'middle')
        .text((d: any) => (d.individualResults[k].satisfiesPreservation.ok ? '' : 'f'));
    }
    g.append('rect')
      .attr('y', (6 + (aggregate ? 1 : 0)) * rowHeight)
      .attr('width', barSlotWidth)
      .attr('height', rowHeight - barPadding)
      .attr('fill', (d: any) => {
        return d.sameFlowValueAcrossAlgorithms.ok ? okColor : failColor;
      });
    g.append('text')
      .attr('x', barSlotWidth / 2)
      .attr('y', (7 + (aggregate ? 1 : 0)) * rowHeight - 4)
      .attr('width', barSlotWidth)
      .attr('height', rowHeight - barPadding)
      .attr('text-anchor', 'middle')
      .text((d: any) => {
        return d.sameFlowValueAcrossAlgorithms.ok ? '' : 'f';
      });
  });
  d3.select(statusBoxStacks.node()).raise();
}

// todo
// old code for displaying too much data to render bar chart
// Did not like the position anyway.
//
// {((contentWidth || 0) - labelWidth - labelPaddingLeft) /
//             results.length /
//             noDisplayedAlgorithms <
//             2 && (
//             <>
//               <p />
//               <small>Too much data to render non-aggregated.</small>
//             </>
//           )}
